/**
 * The two sign-in numbers that both sides of the wire state.
 *
 * `.docs/00-index.md` §6: "Magic link: 15 minutes, single use." `mockups/client/
 * login.html` prints both — "The link is good for **15 minutes** and one use", and
 * a resend button that reads "Resend in 0:44" — so the login screen is not free to
 * have its own opinion about either.
 *
 * They live here rather than in `apps/api` because `apps/web` may import that
 * package's **types only** (`ARCHITECTURE.md` §Repository), and a number is not a
 * type. `modules/auth/auth.ts` re-exports both under the names it already used, so
 * every existing caller is unchanged and there is still one value each.
 *
 * The resend interval is why it matters that this is a shared constant rather than
 * two: the API's throttle answers `{ status: true }` whether or not it sent
 * anything, so the login screen cannot learn the deadline from the response and
 * computes it from the moment it asked. If the two numbers drifted, the button
 * would go live before the server would send.
 */
/** How long a sign-in link works for. Single use, enforced by Better Auth. */
export const MAGIC_LINK_TTL_SECONDS = 15 * 60;
/** One sign-in link per address per minute, counted over `email_outbox`. */
export const MAGIC_LINK_RESEND_SECONDS = 60;
/**
 * The `code` on the `400` that `/sign-in/magic-link` answers for an address with no
 * account.
 *
 * Better Auth's own word for the same fact on its endpoints that look a person up
 * (`/reset-password`, three of `email-otp`'s), so the login screen reads it the way
 * it reads every code Better Auth sends: `routes/login.tsx` `REFUSALS`, lower-cased.
 * Both sides state it for the reason the two numbers above are here — a drift would
 * not fail a build or either side's tests, it would put the API's own sentence on the
 * screen in place of the one that says what to do.
 *
 * Until 14 September 2026 there was no such answer: an unknown address got
 * `{ status: true }` like a sent link, so the form could not be used to learn which
 * addresses have accounts. The owner chose the answer over the oracle; see
 * `.docs/04-auth-clients.md` §Edge cases.
 */
export const NO_ACCOUNT_CODE = 'USER_NOT_FOUND';
/**
 * The identity providers a deployment may offer beside the magic link.
 *
 * Both sides state it, which is the same argument the two numbers above make: the
 * API keys `socialProviders` by these strings (`modules/auth/auth.ts`
 * `socialProvidersFor`) and the SPA hands one straight to `signIn.social`, so a
 * fifth spelling on either side is a button that 404s at the callback rather than a
 * type error.
 *
 * **Which of them a deployment actually offers is a runtime question** — a box may
 * have Google credentials and not Microsoft, or neither — and `GET
 * /api/auth-methods` is what answers it. This list is the vocabulary, not the
 * configuration.
 */
export const SOCIAL_PROVIDERS = ['google', 'microsoft'];
/* ------------------------------------------------------- the OAuth browser leg */
/**
 * The two SPA addresses Better Auth's OAuth provider sends a browser to, and the
 * one API address that resumes the flow after a sign-in.
 *
 * They are here for the reason the two numbers above are: both sides state them and
 * a drift is silent. `modules/auth/auth.ts` hands the first two to the plugin as
 * `loginPage` and `consentPage`; `apps/web` owns the routes behind them and builds
 * the third as a `callbackURL`. Getting one wrong does not fail a build or a test
 * that does not walk the browser — it 404s in somebody's address bar, which is
 * exactly what happened to `/oauth/consent` between sessions 05 and 29.
 *
 * `OAUTH_AUTHORIZE_PATH` is `AUTH_BASE_PATH + '/oauth2/authorize'`, spelled out
 * because `packages/shared` does not know where Better Auth is mounted and the two
 * apps that do both spell `/api/auth` themselves.
 */
export const OAUTH_LOGIN_PATH = '/login';
export const OAUTH_CONSENT_PATH = '/oauth/consent';
export const OAUTH_AUTHORIZE_PATH = '/api/auth/oauth2/authorize';
/**
 * The parameters Better Auth adds to a consent or login URL to sign it, which are
 * therefore the ones that are **not** part of the authorization request.
 *
 * `@better-auth/oauth-provider` signs the whole query with the app secret before it
 * redirects a browser at one of the pages above (`signParams`), and verifies it over
 * every parameter but `sig` when the page hands it back (`verifyOAuthQueryParams`).
 * So the query travels **verbatim** — nothing may be added, dropped or reordered on
 * the way through the SPA — and the only place these names are needed is the one
 * place that has to take them *off* again: resuming `/oauth2/authorize` after a
 * sign-in, which wants the original request and not the signature over it.
 *
 * `ba_param` is the list of names the signature covers, `ba_iat` when it was issued,
 * `ba_pl` which session a post-login step was cleared for, `exp` its deadline and
 * `sig` the signature itself.
 */
export const OAUTH_SIGNATURE_PARAMS = ['sig', 'exp', 'ba_iat', 'ba_pl', 'ba_param'];
//# sourceMappingURL=auth.js.map